import { FC, useMemo } from "react";
import { SiteItem, SuccinctSite } from "@vviinn/user-managment-client-ts";
import { Globe } from "lucide-react";

import {
  DetailedCard,
  DetailKeynote,
  SubtitleItem,
} from "../../components/GeneralUI/DetailedCardComponents";
import { AppearedContent } from "../../components/DataDisplay/AppearedContent";
import { callableNetFeatures } from "../index";
import { useAppDispatch, changeActiveSite } from "../../store";
import { styledIcon } from "../../ui/layout/general/styledIcon";
import { Badge } from "../../ui/layout/molecules/Badge/Badge";
import { campaignTypeTitles } from "../../campaign/constants";

const BADGE_ICON_STYLES = "mr-1 text-stone-400 dark:text-stone-500";

type SiteCardWrapperProps = {
  companyId: string;
  site: SuccinctSite;
  isExtended: boolean;
  lowercaseSearch: string;
  isRestricted?: boolean;
  siteDetails?: SiteItem;
};

export const SiteCardWrapper: FC<
  React.PropsWithChildren<SiteCardWrapperProps>
> = ({
  companyId,
  site,
  isExtended,
  lowercaseSearch,
  isRestricted = false,
  siteDetails,
}) => {
  const dispatch = useAppDispatch();
  const domainSubtitle = useMemo(() => {
    return {
      type: "text",
      text: siteDetails?.domain ?? site.domain,
    } as SubtitleItem;
  }, [siteDetails]);

  const vectorizers = useMemo(() => {
    const vectorizersEnabled =
      isRestricted ||
      !siteDetails ||
      !(
        callableNetFeatures.some((title) => siteDetails[title] !== undefined) ||
        siteDetails.useBarraquito
      );
    if (vectorizersEnabled) return undefined;
    const enabledVectorizers = callableNetFeatures
      .filter((title) => siteDetails[title])
      .map((title) => title.slice(7, -8));
    if (siteDetails.useBarraquito) enabledVectorizers.push("Barraquito");
    return enabledVectorizers;
  }, [siteDetails]);

  return (
    <DetailedCard
      link={`/company/${companyId}/site/${site.id}`}
      icon={<Globe />}
      isExtended={isExtended}
      title={siteDetails?.description}
      onClick={() => {
        dispatch(changeActiveSite(site));
      }}
      subtitles={
        isRestricted || !siteDetails || !vectorizers
          ? [domainSubtitle]
          : [
              domainSubtitle,
              {
                type: "object",
                items: [
                  {
                    title: "AI methods",
                    value: `${vectorizers.length}`,
                  },
                ],
              },
            ]
      }
      appearingSubtitles={isRestricted || siteDetails ? 1 : 2}
      isFilteredOut={
        lowercaseSearch.length > 0 &&
        !site.domain.toLowerCase().includes(lowercaseSearch)
      }
    >
      {!isRestricted && (
        <DetailKeynote items={vectorizers} appearingItems={1} />
      )}

      <div className="mt-4 flex w-full flex-row flex-wrap gap-2">
        {siteDetails ? (
          siteDetails.campaignTypes.length > 0 ? (
            siteDetails.campaignTypes.map((campaignType, index) => (
              <Badge
                key={index}
                type="neutral"
                title={campaignTypeTitles[campaignType]}
                icon={styledIcon(campaignType, BADGE_ICON_STYLES)}
              />
            ))
          ) : (
            <Badge type="warning" title="No Campaigns" />
          )
        ) : (
          <AppearedContent
            isLoaded={false}
            skeletonOptions={{
              amount: 4,
              skeletonStyle: "rowNormalStyle",
            }}
          />
        )}
      </div>
    </DetailedCard>
  );
};
